import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { deriveOrderNumber, isOrderNumber } from "./order-number.js";

export const COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION =
  "COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_V1" as const;
export const COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CAPACITY = 5_000;

export const CommissionPayableRecordNumberSchema = Type.String({
  pattern: "^TY-[0-9]{8}-[0-9A-F]{12}$",
});
export type CommissionPayableRecordNumber = `TY-${string}-${string}`;

export const CommissionPayableServiceStatusSchema = Type.Union([
  Type.Literal("COMPLETED"),
  Type.Literal("EARLY_ENDED"),
  Type.Literal("ABNORMAL_TERMINATED"),
  Type.Literal("REVERSED"),
]);
export type CommissionPayableServiceStatus = Static<typeof CommissionPayableServiceStatusSchema>;

export const CommissionPayableModeSchema = Type.Union([
  Type.Literal("PERCENTAGE"),
  Type.Literal("FIXED"),
  Type.Literal("MONTHLY_TIERED"),
]);
export type CommissionPayableMode = Static<typeof CommissionPayableModeSchema>;

const COMMISSION_PAYABLE_MODE_TERMS = {
  PERCENTAGE: {
    label: "按服务费比例",
    explanation: "按这笔服务结算时冻结的提成比例计算，后来改规则不会改写本行。",
  },
  FIXED: {
    label: "每笔固定提成",
    explanation: "按这笔服务结算时冻结的固定金额计算，后来改规则不会改写本行。",
  },
  MONTHLY_TIERED: {
    label: "月度阶梯累计差额",
    explanation: "按截至这笔服务时的整月累计结果计算本次差额，不是只用本行时长乘一个比例。",
  },
} as const satisfies Record<CommissionPayableMode, { label: string; explanation: string }>;

export function commissionPayableModeTerm(mode: CommissionPayableMode): {
  label: string;
  explanation: string;
} {
  return COMMISSION_PAYABLE_MODE_TERMS[mode];
}

const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;
const SOURCE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function fortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveCommissionPayableRecordNumber(
  businessDate: string,
  serviceId: string,
): CommissionPayableRecordNumber {
  if (!isCanonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!SOURCE_ID_PATTERN.test(serviceId)) {
    throw new TypeError("serviceId must contain at least 12 safe characters");
  }
  return `TY-${businessDate.replaceAll("-", "")}-${fortyEightBitHex(
    `TY\0${businessDate}\0${serviceId}`,
  )}`;
}

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const SignedSafeIntegerSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const SafeIntegerSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const MonthSchema = Type.String({ pattern: "^[0-9]{4}-(?:0[1-9]|1[0-2])$" });
const SyncStateSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("SYNCED"),
  Type.Literal("CONFLICT"),
  Type.Literal("FAILED"),
  Type.Literal("ISOLATED"),
]);

const PublicItemProperties = {
  payableRecordNumber: CommissionPayableRecordNumberSchema,
  orderNumber: Type.String({ pattern: "^DD-[0-9]{8}-[0-9A-F]{12}$" }),
  orderBusinessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
  serviceBusinessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
  coachNumber: Type.String({ minLength: 1, maxLength: 80 }),
  coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
  tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
  serviceTypeCode: Type.String({ minLength: 1, maxLength: 32 }),
  serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
  assignmentType: Type.Union([Type.Literal("ROTATION"), Type.Literal("REQUESTED")]),
  serviceStatus: CommissionPayableServiceStatusSchema,
  billedMinutes: SafeIntegerSchema,
  serviceFeeFen: SignedSafeIntegerSchema,
  commissionBaseFen: SignedSafeIntegerSchema,
  commissionFen: SignedSafeIntegerSchema,
  commissionMonth: MonthSchema,
  commissionMode: CommissionPayableModeSchema,
  commissionRateBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
  fixedCommissionFen: SafeIntegerSchema,
  tierCalculationMode: Type.Union([
    Type.Literal("SEGMENTED_PROGRESSIVE"),
    Type.Literal("WHOLE_MONTH"),
    Type.Null(),
  ]),
  ruleVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  reason: Type.Union([Type.String({ minLength: 2, maxLength: 500 }), Type.Null()]),
  offlineOrigin: Type.Boolean(),
  syncState: SyncStateSchema,
  occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
} as const;

export const CommissionPayableBusinessMetricDetailSourceItemSchema = Type.Object(
  {
    serviceId: StableIdSchema,
    coachId: StableIdSchema,
    orderId: StableIdSchema,
    ruleId: StableIdSchema,
    ...PublicItemProperties,
  },
  { additionalProperties: false },
);
export type CommissionPayableBusinessMetricDetailSourceItem = Static<
  typeof CommissionPayableBusinessMetricDetailSourceItemSchema
>;

export const CommissionPayableBusinessMetricDetailItemSchema = Type.Object(PublicItemProperties, {
  additionalProperties: false,
});
export type CommissionPayableBusinessMetricDetailItem = Static<
  typeof CommissionPayableBusinessMetricDetailItemSchema
>;

const EnvelopeProperties = {
  contractVersion: Type.Literal(COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION),
  tenantId: Type.String({ minLength: 1, maxLength: 128 }),
  storeId: Type.String({ minLength: 1, maxLength: 128 }),
  businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
  timeZone: Type.String({ minLength: 1, maxLength: 80 }),
  businessDayCutover: Type.String({ pattern: "^(?:[01][0-9]|2[0-3]):[0-5][0-9]$" }),
  rangeStartedAt: Type.String({ minLength: 1, maxLength: 64 }),
  rangeEndedAt: Type.String({ minLength: 1, maxLength: 64 }),
  generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  coverage: Type.Literal("COMPLETE_TERMINAL_COACH_SERVICE_PAYABLE_LEDGER"),
  valueFen: SignedSafeIntegerSchema,
  factCount: Type.Integer({
    minimum: 0,
    maximum: COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
  earningFactCount: Type.Integer({
    minimum: 0,
    maximum: COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
  reversalFactCount: Type.Integer({
    minimum: 0,
    maximum: COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
} as const;

export const CommissionPayableBusinessMetricDetailSnapshotSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(CommissionPayableBusinessMetricDetailSourceItemSchema, {
      maxItems: COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type CommissionPayableBusinessMetricDetailSnapshot = Static<
  typeof CommissionPayableBusinessMetricDetailSnapshotSchema
>;

export const CommissionPayableBusinessMetricDetailReadModelSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(CommissionPayableBusinessMetricDetailItemSchema, {
      maxItems: COMMISSION_PAYABLE_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type CommissionPayableBusinessMetricDetailReadModel = Static<
  typeof CommissionPayableBusinessMetricDetailReadModelSchema
>;

type DetailEnvelope = Pick<
  CommissionPayableBusinessMetricDetailReadModel,
  | "tenantId"
  | "storeId"
  | "businessDate"
  | "timeZone"
  | "rangeStartedAt"
  | "rangeEndedAt"
  | "generatedAt"
  | "valueFen"
  | "factCount"
  | "earningFactCount"
  | "reversalFactCount"
>;

function envelopeIsConsistent(value: DetailEnvelope): boolean {
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.rangeStartedAt) ||
    !isCanonicalTimestamp(value.rangeEndedAt) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    Date.parse(value.rangeEndedAt) <= Date.parse(value.rangeStartedAt) ||
    value.factCount !== value.earningFactCount + value.reversalFactCount
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

function publicItemIsConsistent(
  item: CommissionPayableBusinessMetricDetailItem,
  envelope: DetailEnvelope,
): boolean {
  const occurredAt = Date.parse(item.occurredAt);
  const reversed = item.serviceStatus === "REVERSED";
  return (
    /^TY-[0-9]{8}-[0-9A-F]{12}$/u.test(item.payableRecordNumber) &&
    item.payableRecordNumber.slice(3, 11) === envelope.businessDate.replaceAll("-", "") &&
    isOrderNumber(item.orderNumber) &&
    isCanonicalDate(item.orderBusinessDate) &&
    item.orderNumber.slice(3, 11) === item.orderBusinessDate.replaceAll("-", "") &&
    item.serviceBusinessDate === envelope.businessDate &&
    isCanonicalTimestamp(item.occurredAt) &&
    occurredAt >= Date.parse(envelope.rangeStartedAt) &&
    occurredAt < Date.parse(envelope.rangeEndedAt) &&
    occurredAt <= Date.parse(envelope.generatedAt) &&
    (reversed
      ? item.serviceFeeFen <= 0 && item.commissionBaseFen <= 0 && item.commissionFen <= 0
      : item.serviceFeeFen >= 0 && item.commissionBaseFen >= 0 && item.commissionFen >= 0) &&
    (item.commissionMode === "MONTHLY_TIERED") === (item.tierCalculationMode !== null) &&
    item.coachNumber === item.coachNumber.trim() &&
    item.coachDisplayName === item.coachDisplayName.trim() &&
    item.tableNumber === item.tableNumber.trim() &&
    item.serviceTypeCode === item.serviceTypeCode.trim() &&
    item.serviceTypeName === item.serviceTypeName.trim()
  );
}

function itemsAreConsistent(
  items: readonly CommissionPayableBusinessMetricDetailItem[],
  envelope: DetailEnvelope,
): boolean {
  if (items.length !== envelope.factCount) return false;
  const numbers = new Set<string>();
  let valueFen = 0;
  let earningFactCount = 0;
  let reversalFactCount = 0;
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  let previousNumber = "";
  for (const item of items) {
    const occurredAt = Date.parse(item.occurredAt);
    if (
      numbers.has(item.payableRecordNumber) ||
      !publicItemIsConsistent(item, envelope) ||
      occurredAt > previousOccurredAt ||
      (occurredAt === previousOccurredAt &&
        previousNumber !== "" &&
        item.payableRecordNumber <= previousNumber)
    ) {
      return false;
    }
    valueFen += item.commissionFen;
    if (!Number.isSafeInteger(valueFen)) return false;
    if (item.serviceStatus === "REVERSED") reversalFactCount += 1;
    else earningFactCount += 1;
    numbers.add(item.payableRecordNumber);
    previousOccurredAt = occurredAt;
    previousNumber = item.payableRecordNumber;
  }
  return (
    valueFen === envelope.valueFen &&
    earningFactCount === envelope.earningFactCount &&
    reversalFactCount === envelope.reversalFactCount
  );
}

function sourceItemIsConsistent(
  item: CommissionPayableBusinessMetricDetailSourceItem,
  envelope: DetailEnvelope,
): boolean {
  try {
    return (
      publicItemIsConsistent(item, envelope) &&
      item.payableRecordNumber ===
        deriveCommissionPayableRecordNumber(item.serviceBusinessDate, item.serviceId) &&
      item.orderNumber === deriveOrderNumber(item.orderBusinessDate, item.orderId)
    );
  } catch {
    return false;
  }
}

export function isCommissionPayableBusinessMetricDetailSnapshot(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is CommissionPayableBusinessMetricDetailSnapshot {
  if (
    !Value.Check(CommissionPayableBusinessMetricDetailSnapshotSchema, value) ||
    !envelopeIsConsistent(value) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  const serviceIds = new Set<string>();
  for (const item of value.items) {
    if (serviceIds.has(item.serviceId) || !sourceItemIsConsistent(item, value)) return false;
    serviceIds.add(item.serviceId);
  }
  return itemsAreConsistent(value.items, value);
}

export function isCommissionPayableBusinessMetricDetailReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is CommissionPayableBusinessMetricDetailReadModel {
  return (
    Value.Check(CommissionPayableBusinessMetricDetailReadModelSchema, value) &&
    envelopeIsConsistent(value) &&
    (expectedScope === undefined ||
      (value.tenantId === expectedScope.tenantId &&
        value.storeId === expectedScope.storeId &&
        value.businessDate === expectedScope.businessDate)) &&
    itemsAreConsistent(value.items, value)
  );
}
